import { useState } from "react";
import { motion } from "framer-motion";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Check, ArrowUpRight, Download, FileSignature } from "lucide-react";
import { NOTE_TERMS, RETURNS_MATRIX, FUND_PILLARS, SIGN_STEPS, COMPANY, NOTE_CALC, EQUITY, RAISE_PROGRESS, AFTER_INVEST } from "@/data/content";
import { SectionLabel, Reveal } from "@/components/site/primitives";
import { EmailScenarioBar } from "@/components/site/EmailScenarioBar";

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(
    isFinite(n) ? n : 0
  );
const num = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(isFinite(n) ? n : 0);

// $100K path comparison at max 40% bonus — note total includes 5 years of cash interest.
const PATH_CHART_DATA = NOTE_CALC.exits.map((s) => {
  const price = (s.equityM * 1e6) / EQUITY.fdShares;
  const principal = 100000;
  const maxBonus = Math.max(...NOTE_CALC.bonuses.map((b) => b.value));
  return {
    scenario: s.label,
    note: Math.round((principal / NOTE_CALC.convPrice) * (1 + maxBonus) * price + principal * NOTE_CALC.interestRate * NOTE_CALC.termYears),
    equity: Math.round((principal / EQUITY.priceNum) * price),
  };
});

const PathTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div data-testid="path-chart-tooltip" className="border border-epic-lineHover bg-epic-surface px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-epic-sub mb-1">{label}</p>
      {payload.map((p) => (
        <p
          key={p.dataKey}
          className="font-grotesk font-bold text-lg leading-snug"
          style={{ color: p.dataKey === "note" ? "#FF3B30" : "#888888" }}
        >
          {p.dataKey === "note" ? "Note" : "Equity"} — {usd(p.value)}
        </p>
      ))}
    </div>
  );
};

const NoteCalculator = () => {
  const [amount, setAmount] = useState(100000);
  const [bonus, setBonus] = useState(0.4);
  const [compare, setCompare] = useState(true);
  const principal = amount > 0 ? amount : 0;

  const baseShares = principal / NOTE_CALC.convPrice;
  const shares = baseShares * (1 + bonus);
  const equityShares = principal / EQUITY.priceNum;
  const quarterly = (principal * NOTE_CALC.interestRate) / 4;
  const totalInterest = principal * NOTE_CALC.interestRate * NOTE_CALC.termYears;

  const handleInput = (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    setAmount(digits ? parseInt(digits, 10) : 0);
  };

  return (
    <Reveal delay={0.1} className="md:col-span-12 border border-epic-line bg-epic-surface p-6 md:p-10">
      <div data-testid="note-calculator">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-accent mb-6">
          Calculate Your Note
        </p>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end mb-10">
          <div className="md:col-span-7">
            <label className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub">
              Note amount
            </label>
            <div className="relative mt-3">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 font-grotesk font-black text-2xl text-epic-sub">$</span>
              <input
                type="text"
                inputMode="numeric"
                data-testid="note-calc-input"
                value={num(amount)}
                onChange={handleInput}
                className="w-full bg-epic-bg border border-epic-line focus:border-epic-accent outline-none pl-10 pr-4 py-4 font-grotesk font-black text-2xl md:text-3xl text-epic-text tracking-tight transition-colors duration-200"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {NOTE_CALC.presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`note-calc-preset-${p}`}
                  onClick={() => setAmount(p)}
                  className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-200 ${
                    amount === p ? "border-epic-accent text-epic-accent" : "border-epic-line text-epic-sub hover:border-epic-lineHover"
                  }`}
                >
                  {p >= 1000000 ? `$${p / 1000000}M` : `$${p / 1000}K`}
                </button>
              ))}
            </div>
            {principal > 0 && principal < NOTE_CALC.min && (
              <p data-testid="note-calc-min-warning" className="font-mono text-[11px] text-epic-accent mt-3">
                Minimum note investment is {usd(NOTE_CALC.min)}.
              </p>
            )}

            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub mt-8 mb-3">
              Bonus-share election
            </p>
            <div className="flex flex-wrap gap-2">
              {NOTE_CALC.bonuses.map((b) => (
                <button
                  key={b.label}
                  type="button"
                  data-testid={`note-calc-bonus-${b.value * 100}`}
                  onClick={() => setBonus(b.value)}
                  className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-200 ${
                    bonus === b.value ? "border-epic-accent text-epic-accent" : "border-epic-line text-epic-sub hover:border-epic-lineHover"
                  }`}
                >
                  {b.label} ({b.value * 100}%)
                </button>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 border-l-0 md:border-l border-epic-line md:pl-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub">
              Interest — 10% paid quarterly
            </p>
            <p data-testid="note-calc-interest-quarterly" className="font-grotesk font-black text-4xl md:text-5xl text-epic-text tracking-tighter mt-2">
              {usd(quarterly)}
            </p>
            <p className="font-mono text-[11px] text-epic-sub/60 mt-1">
              per quarter · {usd(totalInterest)} over the 5-year term
            </p>
            <div className="mt-5 pt-5 border-t border-epic-line">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub">
                Shares on full conversion at {COMPANY.conversionPrice}
              </p>
              <p data-testid="note-calc-shares" className="font-grotesk font-black text-3xl md:text-4xl text-epic-accent tracking-tighter mt-2">
                {num(Math.floor(shares))}
              </p>
              {bonus > 0 && (
                <p className="font-mono text-[11px] text-epic-sub/60 mt-1">
                  incl. {num(Math.floor(shares - baseShares))} bonus shares
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-sub">
            Note value at 5-year exit — by scenario
          </p>
          <button
            type="button"
            data-testid="note-calc-compare-toggle"
            onClick={() => setCompare((c) => !c)}
            className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-200 ${
              compare ? "border-epic-accent text-epic-accent" : "border-epic-line text-epic-sub hover:border-epic-lineHover"
            }`}
          >
            {compare ? `Comparing vs direct equity at ${EQUITY.price}` : "Compare with direct equity"}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          {NOTE_CALC.exits.map((s) => {
            const price = (s.equityM * 1e6) / EQUITY.fdShares;
            const value = shares * price;
            const multiple = principal > 0 ? value / principal : 0;
            const eqValue = equityShares * price;
            const eqMultiple = principal > 0 ? eqValue / principal : 0;
            const noteTotal = value + totalInterest;
            const noteTotalX = principal > 0 ? noteTotal / principal : 0;
            const noteWins = noteTotal >= eqValue;
            return (
              <div
                key={s.key}
                data-testid={`note-calc-exit-${s.key}`}
                className="border border-epic-line bg-epic-bg p-5 md:p-6 transition-colors duration-300 hover:border-epic-lineHover"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-epic-accent">
                      {s.label}
                    </p>
                    <p className="font-mono text-[10px] text-epic-sub/60 mt-1">
                      ≈ ${price.toFixed(2)} / share
                    </p>
                  </div>
                  {compare && (
                    <span
                      data-testid={`note-calc-winner-${s.key}`}
                      className={`shrink-0 font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-1 border ${
                        noteWins ? "border-epic-accent text-epic-accent" : "border-epic-line text-epic-sub"
                      }`}
                    >
                      {noteWins ? "Note wins" : "Equity wins"}
                    </span>
                  )}
                </div>
                {compare ? (
                  <>
                    <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-epic-sub/70 mt-4">
                      Note — conversion + interest
                    </p>
                    <p className="font-grotesk font-black text-2xl md:text-3xl text-epic-text tracking-tighter mt-1.5 leading-none">
                      {usd(noteTotal)}
                    </p>
                    <p className="font-mono text-[10px] text-epic-sub/60 mt-1">
                      {noteTotalX.toFixed(1)}× — {usd(value)} at exit + {usd(totalInterest)} interest
                    </p>
                    <div className="mt-4 pt-4 border-t border-epic-line" data-testid={`note-calc-eq-${s.key}`}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-epic-sub/70">
                        Direct equity at {EQUITY.price}
                      </p>
                      <p className="font-grotesk font-bold text-lg text-epic-sub tracking-tight mt-1.5">
                        {usd(eqValue)}
                      </p>
                      <p className="font-mono text-[10px] text-epic-sub/60 mt-1">
                        {eqMultiple.toFixed(1)}× — no yield along the way
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="font-grotesk font-black text-2xl md:text-3xl text-epic-text tracking-tighter mt-3 leading-none">
                      {usd(value)}
                    </p>
                    <p className="font-mono text-[11px] text-epic-sub mt-2">
                      {multiple.toFixed(1)}× on principal
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {compare && (() => {
          const results = NOTE_CALC.exits.map((s) => {
            const price = (s.equityM * 1e6) / EQUITY.fdShares;
            return { label: s.label, noteWins: shares * price + totalInterest >= equityShares * price };
          });
          const noteWins = results.filter((r) => r.noteWins).map((r) => r.label);
          const eqWins = results.filter((r) => !r.noteWins).map((r) => r.label);
          const spread = 1 / EQUITY.priceNum - (1 + bonus) / NOTE_CALC.convPrice;
          const breakeven = spread > 0 ? (NOTE_CALC.interestRate * NOTE_CALC.termYears) / spread : null;
          const implied = NOTE_CALC.exits
            .map((s) => `$${((s.equityM * 1e6) / EQUITY.fdShares).toFixed(2)}`)
            .join(", ");
          return (
            <div data-testid="note-calc-verdict" className="mt-5 border-l-2 border-epic-accent pl-4">
              <p className="font-mono text-xs text-epic-text leading-relaxed">
                All things equal:{" "}
                {noteWins.length > 0 &&
                  (noteWins.length === 3
                    ? "the note wins every modeled scenario"
                    : `the note wins in ${noteWins.join(" and ")}`)}
                {noteWins.length > 0 && eqWins.length > 0 && "; "}
                {eqWins.length > 0 &&
                  (eqWins.length === 3
                    ? "direct equity wins every modeled scenario"
                    : `direct equity pulls ahead only in the ${eqWins.join(" and ")}`)}
                .
              </p>
              <p data-testid="note-calc-breakeven" className="font-mono text-[11px] text-epic-sub mt-2 leading-relaxed">
                {breakeven
                  ? `Breakeven at your ${bonus * 100}% bonus election: equity wins only if the exit price beats $${breakeven.toFixed(2)}/share. The modeled downside, base, and upside imply ${implied}.`
                  : `At your ${bonus * 100}% bonus election, the note's yield means it wins at every modeled exit price.`}
              </p>
            </div>
          );
        })()}

        {/* Email capture */}
        <EmailScenarioBar
          endpoint="/api/note-scenario"
          payload={{ amount: principal, bonus }}
          testId="note-calc"
        />

        <p className="font-mono text-[11px] text-epic-sub/50 mt-6">
          Illustrative only. Assumes full conversion at {COMPANY.conversionPrice}/share with the selected bonus election;
          scenario values use the model's 5-year equity values on a current fully-diluted basis of {num(EQUITY.fdShares)} shares,
          before dilution from this raise. The winner comparison counts the note's cash interest and uses the same modeled
          exit prices for both paths. Excludes fees and taxes. Not a guarantee of any return.
        </p>
      </div>
    </Reveal>
  );
};

export const TheNote = () => {
  return (
    <section
      id="the-raise"
      data-testid="raise-section"
      className="mx-auto max-w-[1400px] px-5 md:px-10 py-28 md:py-44"
    >
      <SectionLabel index="[ 05 ]" testId="raise-label">
        The Raise — Convertible Note
      </SectionLabel>

      <Reveal>
        <h2 className="font-grotesk font-black uppercase tracking-tighter text-4xl md:text-6xl lg:text-7xl text-epic-text leading-[0.9] max-w-4xl mb-4">
          Yield now.<br />
          <span className="text-epic-accent">Equity</span> later.
        </h2>
        <p className="font-mono text-sm md:text-base text-epic-sub max-w-2xl leading-relaxed mb-8">
          Raising {COMPANY.raise} via an {COMPANY.instrument} — designed to pay a fixed yield today while locking in a discounted equity position at {COMPANY.conversionPrice}/share.
        </p>

        {RAISE_PROGRESS.committed != null ? (
          <div data-testid="raise-progress" className="max-w-2xl mb-16 md:mb-24">
            <div className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mb-3">
              <span>Raise progress</span>
              <span className="text-epic-text">
                {Math.min(100, (RAISE_PROGRESS.committed / 2500000) * 100).toFixed(0)}% committed
              </span>
            </div>
            <div className="h-1.5 w-full bg-epic-line/40">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${Math.min(100, (RAISE_PROGRESS.committed / 2500000) * 100)}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="h-full bg-epic-accent"
              />
            </div>
            <p className="font-mono text-[11px] text-epic-sub/60 mt-2">
              {usd(RAISE_PROGRESS.committed)} of {COMPANY.raise} target
            </p>
          </div>
        ) : (
          <p
            data-testid="raise-progress-open"
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub max-w-2xl mb-16 md:mb-24"
          >
            Allocations open · {COMPANY.raise} target
          </p>
        )}
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        {/* Note terms */}
        <Reveal className="md:col-span-6 border border-epic-line bg-epic-surface p-6 md:p-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mb-8">
            Note Highlights
          </p>
          <ul className="space-y-4">
            {NOTE_TERMS.map((t, i) => (
              <li key={i} className="flex gap-4" data-testid={`note-term-${i}`}>
                <Check className="w-4 h-4 text-epic-accent shrink-0 mt-1" strokeWidth={3} />
                <span className="font-mono text-sm text-epic-text leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* Returns */}
        <Reveal delay={0.1} className="md:col-span-6 border border-epic-line bg-epic-surface p-6 md:p-10 flex flex-col">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-text mb-2">
            Illustrative Return — $100K Note
          </p>
          <p className="font-mono text-[11px] text-epic-text mb-8">
            Return multiples at 5-year exit, by bonus-share level.
          </p>
          <div className="flex-1 flex flex-col justify-center" data-testid="returns-matrix">
            {/* header */}
            <div className="grid grid-cols-4 gap-2 pb-3 border-b border-epic-line">
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-epic-text">
                Bonus
              </span>
              {RETURNS_MATRIX.scenarios.map((s) => (
                <span key={s} className="font-mono text-[10px] uppercase tracking-[0.12em] text-epic-text text-right">
                  {s}
                </span>
              ))}
            </div>
            {/* rows */}
            {RETURNS_MATRIX.rows.map((row) => (
              <div
                key={row.bonus}
                className="grid grid-cols-4 gap-2 items-baseline py-4 border-b border-epic-line last:border-0"
                data-testid={`return-row-${row.bonus.split(" ")[0].toLowerCase()}`}
              >
                <span className="font-mono text-xs uppercase tracking-[0.1em] text-epic-text">
                  {row.bonus}
                </span>
                {row.multiples.map((m, i) => (
                  <span key={i} className="flex flex-col items-end text-right">
                    <span className="font-grotesk font-black text-xl md:text-2xl tracking-tighter text-epic-accent leading-none">
                      {m}
                    </span>
                    <span className="font-mono text-[10px] text-epic-sub mt-1">
                      {row.values[i]}
                    </span>
                  </span>
                ))}
              </div>
            ))}
          </div>
          <p className="font-mono text-[11px] text-epic-sub/60 mt-6 pt-6 border-t border-epic-line">
            Plus 10% annual interest — ~$50K over a full 5-year term. Illustrative only; not a guarantee of return.
          </p>
        </Reveal>

        {/* Use of funds */}
        <Reveal className="md:col-span-12 border border-epic-line bg-epic-surface p-6 md:p-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-sub mb-8">
            Use of Funds
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-10">
            {FUND_PILLARS.map((f, i) => (
              <div key={f.label} className="border-t border-epic-line pt-5" data-testid={`fund-pillar-${i}`}>
                <span className="font-editorial italic text-3xl text-epic-line">0{i + 1}</span>
                <h3 className="font-grotesk font-bold uppercase tracking-tight text-lg text-epic-text mt-3">
                  {f.label}
                </h3>
                <p className="font-mono text-xs text-epic-sub mt-2 leading-relaxed">{f.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Note calculator */}
        <NoteCalculator />

        {/* $100K path comparison chart */}
        <Reveal delay={0.05} className="md:col-span-12 border border-epic-line bg-epic-surface p-6 md:p-10">
          <div data-testid="path-chart">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-accent mb-2">
              The $100K Comparison, Visualized
            </p>
            <p className="font-mono text-[11px] text-epic-sub/60 mb-8">
              Value at 5-year exit by scenario — note at max 40% bonus with interest counted vs direct equity at {EQUITY.price}.
            </p>
            <div className="h-[280px] md:h-[340px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={PATH_CHART_DATA} margin={{ top: 10, right: 0, left: 0, bottom: 0 }} barCategoryGap="28%" barGap={6}>
                  <XAxis
                    dataKey="scenario"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#888888", fontFamily: "JetBrains Mono", fontSize: 11 }}
                    dy={10}
                  />
                  <YAxis hide />
                  <Tooltip content={<PathTooltip />} cursor={{ fill: "#1a1a1a" }} />
                  <Bar dataKey="note" fill="#FF3B30" radius={0} />
                  <Bar dataKey="equity" fill="#3a3a3a" radius={0} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-6 mt-6">
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-epic-sub">
                <span className="w-2.5 h-2.5 bg-epic-accent inline-block" />
                Note (incl. interest)
              </span>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-epic-sub">
                <span className="w-2.5 h-2.5 bg-[#3a3a3a] inline-block" />
                Direct equity
              </span>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Sign the note — DocuSign PowerForm */}
      <Reveal className="mt-4 md:mt-6 border border-epic-line bg-epic-surface p-6 md:p-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-accent mb-4">
              Ready to Invest
            </p>
            <h3 className="font-grotesk font-bold uppercase tracking-tight text-2xl md:text-3xl text-epic-text leading-[0.95]">
              Sign the note in minutes
            </h3>
            <p className="font-mono text-sm text-epic-sub mt-4 leading-relaxed">
              Complete and e-sign the {COMPANY.instrument} securely online via Adobe Acrobat Sign — no account or printing required.
            </p>
          </div>

          <div className="md:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              {SIGN_STEPS.map((step) => (
                <div key={step.n} className="border-t border-epic-line pt-4" data-testid={`sign-step-${step.n}`}>
                  <span className="font-editorial italic text-3xl text-epic-line">{step.n}</span>
                  <h4 className="font-grotesk font-bold uppercase tracking-tight text-base text-epic-text mt-2">
                    {step.title}
                  </h4>
                  <p className="font-mono text-xs text-epic-sub mt-2 leading-relaxed">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          {COMPANY.signUrl ? (
            <a
              href={COMPANY.signUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sign-note-online"
              className="group inline-flex items-center gap-3 bg-epic-accent px-8 py-5 text-white font-mono text-xs uppercase tracking-[0.15em] transition-[background-color,transform] duration-300 hover:bg-epic-accentHover active:scale-95"
            >
              <FileSignature className="w-4 h-4" />
              Sign the Note Online
            </a>
          ) : (
            <span
              data-testid="sign-note-pending"
              className="inline-flex items-center gap-3 border border-epic-line px-8 py-5 text-epic-sub font-mono text-xs uppercase tracking-[0.15em] cursor-not-allowed"
            >
              <FileSignature className="w-4 h-4" />
              Online Signing — Available Soon
            </span>
          )}
          <a
            href={COMPANY.noteUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-note"
            className="group inline-flex items-center gap-3 border border-epic-line px-8 py-5 text-epic-text font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-epic-lineHover"
          >
            <Download className="w-4 h-4" />
            Download the Note
          </a>
          <a
            href={COMPANY.meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="raise-book-meeting"
            className="group inline-flex items-center gap-3 border border-epic-line px-8 py-5 text-epic-text font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:border-epic-lineHover"
          >
            Book a Meeting
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </Reveal>

      {/* After you invest */}
      <Reveal className="mt-4 md:mt-6 border border-epic-line bg-epic-surface p-6 md:p-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-epic-accent mb-8">
          After You Invest
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {AFTER_INVEST.map((s, i) => (
            <div key={s.title} className="border-t border-epic-line pt-4" data-testid={`after-invest-${i}`}>
              <span className="font-editorial italic text-3xl text-epic-line">{s.n}</span>
              <h4 className="font-grotesk font-bold uppercase tracking-tight text-base text-epic-text mt-2">
                {s.title}
              </h4>
              <p className="font-mono text-xs text-epic-sub mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};
